import { markBackendAvailable } from '@affine/core/utils/backend-availability';
import { DebugLogger } from '@affine/debug';
import { UserFriendlyError } from '@affine/error';
import { I18n } from '@affine/i18n';
import { fromPromise, Service } from '@toeverything/infra';

import {
  backPressureRetryAfter,
  reportBackPressure,
} from './back-pressure-notice';
import {
  gatedBackendFetch,
  isExplicitBackendRequestCancellation,
} from './backend-request-gate';
import type { WorkspaceRequestMode } from './request-coordinator';
import type { ServerService } from './server';

const logger = new DebugLogger('affine:fetch');

export type FetchInit = RequestInit & {
  timeout?: number;
  streamlainMode?: WorkspaceRequestMode;
};

export class FetchService extends Service {
  constructor(private readonly serverService: ServerService) {
    super();
  }
  rxFetch = (
    input: string,
    init?: RequestInit & {
      // https://github.com/microsoft/TypeScript/issues/54472
      priority?: 'auto' | 'low' | 'high';
    } & {
      traceEvent?: string;
    }
  ) => {
    return fromPromise(signal => {
      return this.fetch(input, { signal, ...init });
    });
  };

  /**
   * fetch with custom custom timeout and error handling.
   */
  fetch = async (input: string, init?: FetchInit): Promise<Response> => {
    logger.debug('fetch', input);
    const externalSignal = init?.signal;
    if (externalSignal?.aborted) {
      throw externalSignal.reason;
    }
    const abortController = new AbortController();
    // Keep external cancellation connected through response-body consumption.
    const signal = externalSignal
      ? AbortSignal.any([externalSignal, abortController.signal])
      : abortController.signal;

    const timeout = init?.timeout ?? 15000;
    const timeoutId =
      timeout > 0
        ? setTimeout(() => {
            abortController.abort(new Error('timeout after ' + timeout + 'ms'));
          }, timeout)
        : undefined;

    let res: Response;
    try {
      res = await gatedBackendFetch(
        input,
        {
          ...init,
          signal,
          headers: {
            ...init?.headers,
            'x-streamlain-version': BUILD_CONFIG.appVersion,
          },
        },
        {
          baseUrl: this.serverService.server.serverMetadata.baseUrl,
          isCancellation: error =>
            isExplicitBackendRequestCancellation(error, externalSignal),
        }
      );
    } catch (error) {
      if (externalSignal?.aborted) {
        throw externalSignal.reason;
      }
      throw this.networkError(error);
    } finally {
      clearTimeout(timeoutId);
    }

    if (!res.ok) {
      // Receiving an HTTP response proves that the backend transport is
      // reachable. Scoped 5xx responses are handled by their request owner.
      markBackendAvailable();
      const retryAfter = backPressureRetryAfter(res.status, res.headers);
      if (retryAfter !== null && init?.streamlainMode === 'foreground') {
        reportBackPressure(retryAfter);
      }
      if (res.status === 429) {
        logger.debug(
          `Rate limited (429). Retry-After: ${retryAfter ?? 'unknown'}s, Remaining: ${res.headers.get('X-RateLimit-Remaining') ?? 'unknown'}`
        );
        throw new UserFriendlyError({
          status: 429,
          code: 'TOO_MANY_REQUEST',
          type: 'TOO_MANY_REQUEST',
          name: 'TOO_MANY_REQUEST',
          message: I18n['com.streamlain.error.too-many-requests.message'](),
        });
      } else if (res.status === 504) {
        const error = new Error('Gateway Timeout');
        logger.debug('network error', error);
        throw new UserFriendlyError({
          status: 504,
          code: 'NETWORK_ERROR',
          type: 'NETWORK_ERROR',
          name: 'NETWORK_ERROR',
          message: I18n['com.streamlain.error.gateway-timeout.message'](),
          stacktrace: error.stack,
        });
      } else {
        const body: unknown = res.headers
          .get('Content-Type')
          ?.startsWith('application/json')
          ? await res.json()
          : await res.text();
        // The answer's HTTP status is the error's status: a refusal whose
        // body names none (e.g. { code: 'VISIBLE_SOURCE_LIMIT' }) is a 4xx,
        // never the 500 an unknown status would default to.
        const error = UserFriendlyError.fromAny(
          typeof body === 'object' && body !== null
            ? { ...body, status: res.status }
            : { message: body, status: res.status }
        );
        // The back-pressure notice speaks for a busy answer; no error toast.
        if (retryAfter !== null) {
          Object.assign(error, { skipGlobalSWRToast: true });
        }
        throw error;
      }
    }

    markBackendAvailable();
    return res;
  };

  private networkError(error: unknown): UserFriendlyError {
    return new UserFriendlyError({
      status: 504,
      code: 'NETWORK_ERROR',
      type: 'NETWORK_ERROR',
      name: 'NETWORK_ERROR',
      message: I18n['com.streamlain.error.network.message'](),
      stacktrace: error instanceof Error ? error.stack : undefined,
    });
  }
}
